import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CircularProgress } from '@mui/material';
import { Files } from 'icons/untitledui';

// components
import Contributors from 'teamLeadInsights/components/Contributors';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import StyledMarkdown from 'shared/StyledMarkdown';

// helpers
import getShiftContributors from 'teamLeadInsights/helpers/getShiftContributors';

// hooks
import { useSiteShift } from 'shared/hooks/api/shiftNotes';

// styles
import * as Text from 'theme/components/Text.style';
import * as ViewTeamLeadStyles from 'teamLeadInsights/styles/ViewTeamLead.style';

interface ReportContentProps {
  siteId: string;
}

export default function ReportContent({
  siteId,
}: ReportContentProps): JSX.Element {
  const { t } = useTranslation('pageTeamLeadInsights');

  const { data: reportSummaryData, isLoading } = useSiteShift(siteId);

  const currentResponse =
    reportSummaryData && (reportSummaryData as any)?.id === siteId
      ? (reportSummaryData as any)
      : null;
  const hasCurrentSummary = Boolean(currentResponse?.summary);
  const hasContributors =
    !!reportSummaryData && getShiftContributors(reportSummaryData).length > 0;
  const showNoSummary =
    !siteId || (!isLoading && currentResponse && !hasCurrentSummary);

  return (
    <ErrorBoundary>
      <ViewTeamLeadStyles.SiteContent>
        <Box
          role="tabpanel"
          sx={{
            p: 3,
            pb: 0,
            pt: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Text.P2>
            {(() => {
              if (isLoading) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '40px 20px',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                );
              }

              if (showNoSummary) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      padding: '40px 20px',
                      backgroundColor: '#F7F7F5',
                      borderRadius: '16px',
                      border: '1px solid #E8E7E3',
                      minHeight: '200px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '64px',
                        height: '64px',
                        backgroundColor: '#E8E7E3',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px',
                        color: '#7A7A76',
                      }}
                    >
                      <Files.FileX02 size={32} />
                    </Box>
                    <Text.P2>{t('viewTeamLead.NoSummaryAvailable')}</Text.P2>
                  </Box>
                );
              }

              if (hasCurrentSummary) {
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px',
                    }}
                  >
                    <StyledMarkdown content={currentResponse.summary} />
                    {hasContributors && (
                      <>
                        <ViewTeamLeadStyles.Divider />
                        <Contributors shift={reportSummaryData} />
                      </>
                    )}
                  </div>
                );
              }

              return null;
            })()}
          </Text.P2>
        </Box>
      </ViewTeamLeadStyles.SiteContent>
    </ErrorBoundary>
  );
}
